import locationUSCityStateZipInternationalCityModelValidator from 'forms/shared/validators/models/location/models/usCityStateZipInternationalCity';
import locationAddressModelValidator from 'forms/shared/validators/models/location/models/address';
import { OTHER, DEFAULT_LATEST } from 'constants/dateLimits';
import { sortSafeDateTimes, getSafeDateTime } from 'helpers/date';
import { ValidateJSModel } from 'forms/shared/types/validatejs';
import nameModelValidator from 'forms/shared/validators/models/name/name';
import { setToStartOfMonth } from 'helpers/date';
import { isPVQForm } from 'helpers/form';
import { foreignBusinessAffairsSponsorshipReasonOptions } from 'constants/enums/foreignBusinessOptions';
import pvqNameModelValidator from 'forms/shared/validators/models/pvqName/name';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import { isSafeUnitedStates } from 'helpers/locations';
import { hasYesOrNo } from 'models/validate';
import { ForeignBusinessSponsorshipItemModel } from '../types';

export const foreignBusinessSponsorshipItemValidator: ValidateJSModel<ForeignBusinessSponsorshipItemModel> = {
  Name: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return { presence: true, model: { validator: pvqNameModelValidator } };
    }

    return { presence: true, model: { validator: nameModelValidator } };
  },

  Birthdate: (value, attributes) => {
    if (attributes?.BirthdateNotApplicable?.applicable === false) { return {}; }

    return { presence: true, date: OTHER };
  },

  Birthplace: {
    presence: true,
    location: { validator: locationUSCityStateZipInternationalCityModelValidator },
  },

  Address: (value, attributes, attributeName, options) => {
    if (attributes?.AddressNotApplicable?.applicable === false || attributes?.InUsaNotApplicable?.applicable === false) { return {}; }

    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        location: {
          validator: pvqLocationModelValidator,
          requireStreet: isSafeUnitedStates(value),
          requireIsMilitaryDiplomaticAddress: isSafeUnitedStates(value),
          requireMilitaryDiplomaticInstallationName: isSafeUnitedStates(value),
        },
      };
    }

    return { presence: true, location: { validator: locationAddressModelValidator } };
  },

  Citizenship: { hasValue: true, country: true },

  Dates: (value, attributes, attributeName, options) => {
    const { Birthdate } = attributes;
    const { applicantBirthdate } = options;

    const sortedDates = sortSafeDateTimes([
      { ...getSafeDateTime(Birthdate), field: 'Birthdate' },
      { ...getSafeDateTime(applicantBirthdate), field: 'applicantBirthdate' },
    ]);
    const earliestDate = sortedDates.length ? setToStartOfMonth(sortedDates[sortedDates.length - 1]) : null;

    return {
      presence: true,
      daterange: {
        earliest: earliestDate?.datetime,
        latest: DEFAULT_LATEST,
        tooEarly: earliestDate?.field === 'Birthdate' ? 'error.date.sponsoredForeignNationalDateTooEarly' : 'error.date.min'
      },
    };
  },

  Residence: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    return { presence: true, location: { validator: locationAddressModelValidator } };
  },

  Organization: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.WorkedWithPrivateOrganization?.value === 'Yes') {
        return { hasValue: true };
      }

      return {};
    }

    if (attributes?.OrganizationNotApplicable?.applicable === false) { return {}; }

    return { hasValue: true };
  },

  OrganizationAddress: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.WorkedWithPrivateOrganization?.value === 'Yes') {
        return {
          presence: true,
          location: {
            validator: pvqLocationModelValidator,
            requireStreet: isSafeUnitedStates(value),
          }
        };
      }

      return {};
    }

    if (attributes?.OrganizationAddressNotApplicable?.applicable === false) { return {}; }

    return { presence: true, location: { validator: locationAddressModelValidator } };
  },

  Stay: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    return { hasValue: true };
  },

  Sponsorship: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    return { hasValue: true };
  },

  BirthdateNotApplicable: {},

  OrganizationNotApplicable: {},

  OrganizationAddressNotApplicable: {},

  Reason: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        hasValue: {
          validator: {
            inclusion: foreignBusinessAffairsSponsorshipReasonOptions,
          },
        },
      };
    }

    return {};
  },

  ReasonOtherExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.Reason?.value === 'Other') {
      return { hasValue: true };
    }

    return {};
  },

  WorkedWithPrivateOrganization: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return { hasValue: { validator: hasYesOrNo } };
    }

    return {};
  },
};
